/// <reference lib="dom" />
/* eslint-disable no-underscore-dangle */
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale.ts'
import { getLanguageFromLocale } from '@backmarket/nuxt-module-i18n/getLanguageFromLocale'

import type { TrackingConfig } from '../types'

import { getDidomiConfig } from './config'

/**
 * Function that creates the <script> element that will load the Didomi SDK
 * ⚠⚠ THIS FUNCTION SHOULD STAY SELF-CONTAINED ⚠⚠ (no reference to outside functions/modules) as it will be serialised into the SSR HTML
 * Function taken from Didomi and adapted (onerror handling) to our needs
 */
function loadDidomiSdk(apiKey: string, noticeId: string) {
  const { document, window } = globalThis

  const preconnect = document.createElement('link')
  preconnect.rel = 'preconnect'
  preconnect.as = 'script'

  const dnsPrefetch = document.createElement('link')
  dnsPrefetch.rel = 'dns-prefetch'
  dnsPrefetch.as = 'script'

  const script = document.createElement('script')
  script.id = 'spcloader'
  script.type = 'text/javascript'
  script.async = true
  script.charset = 'utf-8'

  script.onerror = () => {
    // in case the event is emitted too early (before hydration), we set this flag so that late subscribers can still rely on it
    window.__didomiLoadError = true

    window.dispatchEvent(
      new CustomEvent('didomi:load-error', {
        detail: { message: 'Failed to load Didomi SDK' },
      }),
    )
  }

  let src = `https://sdk.privacy-center.org/${apiKey}/loader.js?target_type=notice&target=${noticeId}`

  if (window.didomiConfig?.user) {
    const { country, region } = window.didomiConfig.user
    if (country) {
      src = `${src}&country=${country}`
      if (region) {
        src = `${src}&region=${region}`
      }
    }
  }

  preconnect.href = 'https://sdk.privacy-center.org/'
  dnsPrefetch.href = 'https://sdk.privacy-center.org/'
  script.src = src

  const firstScript = document.getElementsByTagName('script')[0]
  const parent = firstScript.parentNode
  if (!parent) return

  parent.insertBefore(preconnect, firstScript)
  parent.insertBefore(dnsPrefetch, firstScript)
  parent.insertBefore(script, firstScript)
}

export function getDidomiScriptContent({
  config,
  countryCode,
  cdnURL,
  locale,
}: {
  config: TrackingConfig
  countryCode: MarketCountryCode
  cdnURL?: string
  locale: Bcp47Locale
}) {
  const { apiKey } = config.didomi
  const noticeId = config.didomi.noticeId[countryCode]

  const language = getLanguageFromLocale(locale)
  return `window.didomiConfig = ${JSON.stringify(getDidomiConfig({ config, countryCode, cdnURL, language }))};
    (${loadDidomiSdk.toString()})(${JSON.stringify(apiKey)}, ${JSON.stringify(noticeId)});`
}
